import { useState, useEffect, useCallback, useMemo } from 'react';
import { getFormatById } from '../registry/formatRegistry';
import { generateSchedule } from '../generators';
import { TEAM_ROSTER_DEFAULT } from '../data/participantConstants';
import { suggestGroupCount, assignGroups, shuffleParticipants } from '../generators/groupingUtils';
import { TIE_FORMATS, getScheduleRoundsArrays } from '../generators/tieFormats';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../services/apiUtil';
import { claimScorerRole } from '../../scoring/services/claimScorer';
import { tournamentAPI, tournamentParticipantAPI, tournamentScheduleAPI } from '../../tournaments/services/tournamentApi';
import { MatchHandlers } from '../../tournaments/utils/matchHandlers';
import { buildTournamentVO } from '../../tournaments/tournamentVO';
import { canManageMatches, isOrganizer, isCoOwner, isScorer } from '../../tournaments/access';
import { saveSchedule as saveScheduleToBackend, loadSavedSchedule, isSaveSupported } from '../data/real/services/scheduleApi';

const EMPTY_FILTERS = {
  courts: [],
  rounds: [],
  groups: [],
  participants: [],
  statuses: [],
  myGamesOnly: false,
  // A specific real person picked from the backend search (see
  // useScheduleBuilder's `searchUsers` and FiltersModal.jsx) — `watchedUserName`
  // is display-only (so the applied badge/panel can show who without
  // re-querying), the actual filtering happens on `watchedUserId` alone.
  watchedUserId: null,
  watchedUserName: '',
};

const storageKey = (tournamentId) => `tf_schedule_builder:${tournamentId}`;

const DEFAULT_COUNT_BY_FORMAT = {
  single_elimination: 8,
  round_robin: 6,
  group_knockout: 12,
  compass_draw: 16,
  regional_bracket: 16,
  page_playoff: 6,
  cross_group: 8,
};

/** The wizard screens, in order — see ScheduleBuilderTab.jsx for how each maps to a component. */
export const WIZARD_STEPS = {
  // Only reached in standalone mode (src/tournament_formats/ExploreTournamentFormatsPage.jsx) —
  // skipped entirely when opened from inside a real tournament's Schedule
  // tab, since that data already exists on the tournament itself.
  TEST_DATA: 'test_data',
  FORMAT: 'format',
  PARAMETERS: 'parameters',
  GROUPS: 'groups',
  RESULT: 'result',
};

const readStored = (tournamentId) => {
  try {
    const raw = localStorage.getItem(storageKey(tournamentId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStored = (tournamentId, value) => {
  try {
    if (value === null) {
      localStorage.removeItem(storageKey(tournamentId));
    } else {
      localStorage.setItem(storageKey(tournamentId), JSON.stringify(value));
    }
  } catch {
    // localStorage unavailable (private mode, quota) — prototype persistence is best-effort only
  }
};

/**
 * Drives the whole step-by-step "pick format -> configure parameters ->
 * configure groups -> generated schedule" wizard for a single tournament,
 * persisting the generated schedule + last view mode to localStorage so it
 * survives tab switches and reloads. This is prototype-phase persistence
 * over mock data — no backend call, intentionally, since backend
 * tournament-format code is under freeze (KYN-475) while the redesign is scoped.
 *
 * Every parameter the generators actually need — format, sport, participant
 * type/count, players per team, legs, grouping — lives in ONE `config`
 * object, seeded once at initialization (from Test Data in standalone mode,
 * or a real tournament's own data via `tournamentContext`) and changed only
 * through `updateConfig(patch)` from then on. Every wizard step reads
 * whatever slice of `config` it needs and calls `updateConfig({ field:
 * value })` directly — there are no per-field props or per-field setters
 * threaded through ScheduleBuilderTab. Adding a new generation parameter
 * (courts, start time, address, ...) means adding one key here and one input
 * in whichever step collects it — not touching this hook's return shape,
 * the tab router, or any other step's props.
 *
 * Deliberately NOT in `config`: `step` (wizard navigation), `groupAssignments`
 * (live per-participant editor state derived from config), `schedule` /
 * `viewMode` (generation output + its display mode), `tieFormat` /
 * `tieOverrides` (post-generation presentation settings for an existing
 * schedule, not an input to generating one), `error`.
 *
 * The Groups step only exists for formats that actually need it — see
 * `needsGroupsStep` below — and even then is skipped when it would be
 * trivial (Dual Meet with exactly 2 teams: the 2 team names already ARE the
 * two sides, nothing to group or assign). `proceedFromParameters` is what
 * decides whether "Next" on the Parameters step reads "Set Groups" or jumps
 * straight to generating.
 *
 * `options.standalone` (the Explore Tournament Formats page — try any format
 * on mock data without a real tournament) adds a Test Data screen in front
 * of everything else, collecting sport/participant type/count by hand.
 * Opened from inside a REAL tournament's Schedule tab instead (the default),
 * that screen is skipped — `options.tournament` + `options.participantsSummary`
 * (both already fetched by TournamentDetailPage.jsx for its other tabs) seed
 * the same fields from that tournament's real data instead, still editable
 * afterward on the Parameters step, never re-asked from scratch.
 *
 * `options.participantsProvider` — `{ getParticipants(participantType, count,
 * playersPerTeam), getMaxParticipants(participantType) }` — is this hook's
 * ONLY way of turning config into actual participant/team objects. This file
 * never imports mockParticipantsService (or any other concrete data source)
 * itself; ScheduleBuilderTab.jsx decides which provider to wire in for each
 * entry point and passes it in here, so swapping mock for a real
 * enrolled-roster provider later never touches this hook or any wizard step
 * — only which provider ScheduleBuilderTab.jsx constructs.
 */
export const useScheduleBuilder = (tournamentId, hookOptions = {}) => {
  const { standalone = false, tournament = null, participantsSummary = null, participantsProvider, onOpenLiveTab } = hookOptions;
  const stored = useMemo(() => readStored(tournamentId), [tournamentId]);

  // Real tournament data, mapped to this wizard's vocabulary — null in
  // standalone mode (nothing to carry forward, Test Data collects it by hand).
  const tournamentContext = useMemo(() => {
    if (standalone || !tournament) return null;
    const sportName = tournament?.sportType?.sportName || tournament?.sport_typea?.sport_name || null;
    const sportPlayedBy = (tournament?.sportType?.playedBy || tournament?.sport_typea?.played_by || null)?.toLowerCase() || null;
    const rawType = tournament?.participantsType || tournament?.participants_type || null;
    const participantsType = ['singles', 'doubles', 'teams'].includes(rawType) ? rawType : null;
    // totalPlayers, NOT totalParticipants: the latter counts every
    // tournament_participants row (see tournamentParticipantsController's
    // summary — it is a bare count with no role filter), so a co-owner,
    // scorer or referee who never plays still inflated it. That made format
    // eligibility claim a tournament was closer to playable than it was —
    // e.g. a tournament whose only enrolment was a non-playing co-owner
    // reported "1 short" of Single Elimination's 2 while the page header,
    // which does count players, correctly showed 0. totalPlayers is the
    // player:true / team_id:null count, which is what a schedule slot needs.
    const enrolledCount = participantsSummary?.totalTeams > 0
      ? participantsSummary.totalTeams
      : participantsSummary?.totalPlayers || null;
    return { sportName, sportPlayedBy, participantsType, enrolledCount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standalone, tournament?.id, tournament?.sportType?.sportName, tournament?.sportType?.playedBy, tournament?.participantsType, participantsSummary?.totalTeams, participantsSummary?.totalPlayers]);

  // This tournament's own configured match settings (bestOf, maxPoints,
  // isRally, winBy2, ...) — the seed the Match/Round Settings gear-icon
  // modal should pre-fill "Use custom settings" from, same as production's
  // ScheduleTab.jsx already does (`tournamentSettings` there — see
  // MatchSettingsModal.jsx's own doc comment for the full port lineage).
  // Without this, checking "Use custom settings" with no prior override
  // fell through to MatchSettingsComponent's generic sport-wide defaults
  // (scoringConfig.js) instead of what THIS tournament actually configured
  // — technically correct in the sense that nothing was overridden yet, but
  // not what an organizer opening the form to make ONE targeted change
  // (say, just the court) would expect to see. `participantsType` is
  // merged in from the tournament's own top-level field, same as
  // production, since a stale value inside `matchConfiguration` should
  // never win over it. `null` in standalone mode (nothing to pre-fill from)
  // — MatchSettingsComponent's existing generic-default fallback still
  // applies there, unchanged.
  const tournamentMatchSettings = useMemo(() => {
    if (standalone || !tournament) return null;
    const parsed = tournament.matchConfiguration && typeof tournament.matchConfiguration === 'object' ? tournament.matchConfiguration : {};
    return tournament.participantsType ? { ...parsed, participantsType: tournament.participantsType } : parsed;
  }, [standalone, tournament]);

  // Standalone (the "Explore Formats" button) always starts at Test Data,
  // even if a previous visit left a generated schedule in localStorage —
  // "Explore" is meant to start fresh every time you open it, not resume
  // wherever you left off. `config` below still seeds from that same
  // storage, so the remembered sport/type/count are still there to walk
  // through again, just not auto-skipped past. Opened from inside a real
  // tournament's Schedule tab instead, resuming at the last generated
  // result is exactly the useful behavior — that tab is scoped to one
  // specific tournament, not a repeatable demo.
  const [step, setStep] = useState(() => {
    if (standalone) return WIZARD_STEPS.TEST_DATA;
    return stored?.schedule ? WIZARD_STEPS.RESULT : WIZARD_STEPS.FORMAT;
  });

  // A real tournament's saved schedule only arrives from the backend (the
  // mount effect below), which is async — so without this the first paint
  // shows whatever `step` seeded above, i.e. the FORMAT wizard, for however
  // long loadSavedSchedule takes. That flash puts "Choose a format" on top of
  // a tournament that already HAS one, which reads as an invitation to
  // regenerate over finished history. localStorage covers the case where this
  // browser generated the schedule, but never a different browser, a cleared
  // cache, or a schedule saved by someone else.
  //
  // Starts true only where a backend load is actually going to run — never in
  // standalone (Explore Formats has nothing to load), and never when
  // localStorage already produced a schedule to render, since there is no
  // blank moment to cover in that case.
  const [isLoadingSaved, setIsLoadingSaved] = useState(() => !standalone && !stored?.schedule);

  // Single source of truth for every schedule-generation input — see module
  // doc comment above for what belongs here vs. stays separate.
  const [config, setConfig] = useState(() => ({
    formatId: stored?.formatId || null,
    sportType: stored?.sportType || tournamentContext?.sportName || null,
    // Lowercased `playedBy` of the selected sport ('team' | 'individual') —
    // the one piece of sport data formatEligibility.js's rule engine
    // actually needs, kept alongside sportType rather than re-derived by
    // every consumer. Seeded from TestDataStep's fetched sport list in
    // standalone mode, or the real tournament's own sportType otherwise.
    sportPlayedBy: stored?.sportPlayedBy || tournamentContext?.sportPlayedBy || null,
    participantType: stored?.participantType || tournamentContext?.participantsType || 'singles',
    participantCount: stored?.participantCount || tournamentContext?.enrolledCount || null,
    // Only meaningful when participantType === 'teams' — how many players
    // from each mock team's roster actually get scheduled (see buildRoster
    // in mockParticipantsService). MLP eligibility specifically requires
    // exactly 4 (see tieFormats.isMLPEligible) — anything else just means
    // no MLP Settings for that schedule, not an error.
    playersPerTeam: stored?.playersPerTeam || TEAM_ROSTER_DEFAULT,
    // Matches per pairing (aka "legs") — how many times the same two sides
    // meet, e.g. 3 for a best-of-3 series. Only meaningful for formats
    // declaring `supportsLegs` (round_robin, cross_group); defaults to 1.
    legs: stored?.legs || 1,
    groupingConfig: stored?.groupingConfig || null,
    // Organizer-entered override for a group/region's display name, keyed
    // by group index (0-based) — e.g. renaming "Group A"/"Group B" to real
    // club names for a Dual Meet ("Parsippany" vs "Livingston"). Only
    // meaningful for `needsGrouping` formats; set from the Configure Groups
    // step, read by getGroupLabel (generators/groupLabels.js) at generation
    // time — a blank entry falls back to the auto-generated default.
    groupNames: stored?.groupNames || {},
    // How many ladder rounds (promotion/relegation cycles) the organizer
    // intends to run — only meaningful for `supportsLadderRounds` formats
    // (ladder); descriptive only, since only Round 1 is ever concretely
    // generated (see ladderGenerator.js). Auto-suggested from participant
    // count (one round per court) until the organizer adjusts it by hand —
    // same pattern as groupingConfig.userAdjustedGroups below.
    ladderRounds: stored?.ladderRounds || null,
    ladderRoundsUserAdjusted: stored?.ladderRoundsUserAdjusted || false,
    // Physical courts/fields the tournament is played on — organizer-named
    // (see CourtsSettingsModal), not auto-numbered by us: whatever label
    // they'd actually announce to players ("Court 1", "Court A", "Field 7")
    // is both the id and the display label. Empty until they configure any,
    // at which point matches start getting auto-assigned across them (see
    // scheduleRows.js). No current source populates this automatically (no
    // real tournament venue/court data exists yet, and TestDataStep doesn't
    // collect it) — organizer-entered only, for now.
    courts: stored?.courts || [],
  }));

  // Accepts either a partial patch or an updater `(prev) => patch`, merged
  // shallowly over the current config — mirrors React's own setState so
  // guarded updates (e.g. "only fill this in if still empty") read the same
  // way they would with a plain useState.
  const updateConfig = useCallback((patch) => {
    setConfig((prev) => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
  }, []);

  // Unlike every other `config` field, courts is a POST-generation display
  // setting (like tieOverrides/matchOrderOverrides), not a generation input
  // — reassigning courts doesn't need a regenerate to take effect, so it
  // shouldn't need one to persist either. Plain `updateConfig` alone doesn't
  // write through to storage immediately (only `generate()` does, since
  // every other config field genuinely does need a regenerate to matter) —
  // this both updates the live `config.courts` and persists it right away,
  // matching `changeTieFormat`'s pattern below.
  const updateCourts = useCallback((courts) => {
    setConfig((prev) => ({ ...prev, courts }));
    const current = readStored(tournamentId);
    if (current) writeStored(tournamentId, { ...current, courts });
  }, [tournamentId]);

  const [groupAssignments, setGroupAssignments] = useState(null); // array of arrays of participants, editor-only state
  const [schedule, setSchedule] = useState(stored?.schedule || null);
  const [viewMode, setViewMode] = useState(stored?.viewMode || null);
  // Tie Format — independent of tournament structure (see generators/tieFormats.js):
  // how an already-scheduled team-vs-team match resolves into games. Only
  // meaningful once a schedule exists, so it isn't reset by selectFormat the
  // way config is — generate() resets it instead.
  const [tieFormat, setTieFormat] = useState(stored?.tieFormat || TIE_FORMATS.SINGLE_MATCH);
  // MLP-only, per-team gender + pairing customization (see TeamPairingConfig) —
  // keyed by team id: { [teamId]: { genders: {...}, excludedPairKeys: [...] } }.
  const [tieOverrides, setTieOverrides] = useState(stored?.tieOverrides || {});
  // Per-round row display order, independent of `schedule` itself — same
  // "separate overlay state, folded in at render time" pattern as
  // tieOverrides above, since nothing here is submitted to a backend
  // (KYN-475) and a fresh generate() naturally invalidates stale entries
  // (new match/row keys). Keyed by which list a reorder happened in — a
  // format can render more than one MatchListView (per-region, per-court,
  // per-direction, ...) — then by round number:
  // `{ [listKey]: { [roundNumber]: [rowKey, rowKey, ...] } }`. See
  // MatchTable.jsx's `moveRow` for how a reorder click produces the value.
  const [matchOrderOverrides, setMatchOrderOverrides] = useState(stored?.matchOrderOverrides || {});
  // Round-level and match-level overrides from the gear-icon settings modal
  // (court reassignment + custom match rules; match-level also gets Team
  // Pairing — which specific roster players — round-level doesn't, since
  // there's no single sensible player choice across every match in a round).
  // Same overlay-state pattern as tieOverrides/matchOrderOverrides above,
  // keyed by listKey then by round number or row key respectively. A row's
  // OWN override always wins over its round's — see scheduleRows.js's
  // `resolveOverrides` — so applying round settings never silently erases a
  // match-specific tweak unless the organizer explicitly chose to clear it
  // (see applyRoundOverride's `clearRowKeys` param).
  const [roundOverrides, setRoundOverrides] = useState(stored?.roundOverrides || {});
  const [rowOverrides, setRowOverrides] = useState(stored?.rowOverrides || {});
  // The Filters panel's applied (not draft) selections — global, not
  // per-listKey like the overrides above, since it's one combined picker
  // covering the whole schedule regardless of which list/tab is currently
  // showing (see FiltersModal.jsx). Reset whenever a fresh schedule is
  // generated, same as every other post-generation display setting — a
  // stale court/round/player selection from a previous generation wouldn't
  // mean anything against a new one.
  const [filters, setFilters] = useState(stored?.filters || EMPTY_FILTERS);
  const [error, setError] = useState(null);

  // Real app user — available whenever someone's actually logged in, same
  // computation regardless of standalone vs. real tournament mode. My Games
  // matching itself (see involvedUserIds in scheduleRows.js) is identical
  // either way: it just checks whether this id shows up as a participant's
  // own id or one of a team's roster ids. Explore Formats' mock data
  // (mockParticipants.json) has this exact id planted on a few entries for
  // testing that match logic against — no separate code path for mock vs.
  // real data, the data is just built to exercise the same real logic.
  const { user, isAuthenticated } = useAuth();
  const currentUserId = isAuthenticated ? user?.id ?? null : null;

  // The logged-in user's own registration row(s) for this tournament — same
  // `checkRegistration` call ScheduleTab.jsx already makes to derive its own
  // `isScorer` state — fetched here instead so this module's Start Scoring
  // gating (see `tournamentVO` below) can reuse the exact same access rules
  // (`src/tournaments/access.js`) rather than re-deriving them. Only the
  // calling user's own rows are needed: `buildTournamentVO`'s `isScorer`
  // check just looks for a `scorer: true` row matching the given user id,
  // it doesn't need every OTHER participant's rows too.
  const [myRegistrationRows, setMyRegistrationRows] = useState([]);
  useEffect(() => {
    if (standalone || !tournamentId || !currentUserId) return;
    let cancelled = false;
    tournamentParticipantAPI.checkRegistration(tournamentId, currentUserId, []).then((res) => {
      if (cancelled) return;
      setMyRegistrationRows(res?.data || res || []);
    }).catch(() => {
      // Not registered, or the lookup failed — Start Scoring just stays
      // gated off for this user, same as production would show.
    });
    return () => {
      cancelled = true;
    };
  }, [standalone, tournamentId, currentUserId]);

  // A minimal Tournament VO (see src/tournaments/tournamentVO.js) — just
  // enough for `isEnabled(..., TOURNAMENT_ENTITIES.GAME_START, ...)` to
  // apply the exact same rules production's GameCard.jsx does (scorer-only,
  // tournament in_progress, game not completed, checkin_status === ready).
  // `rounds: []` is deliberate: GAME_START's own logic never reads
  // `tournamentVO.rounds`/`_computed` (only `.status`/`.organizer_user_id`/
  // `.participants` plus the `{ game }` passed in per-call), so there's no
  // need to fetch/process a second, differently-shaped copy of the rounds
  // this hook already has via `schedule`.
  const tournamentVO = useMemo(() => {
    if (standalone || !tournament) return null;
    return buildTournamentVO(tournament, [], myRegistrationRows, { userId: currentUserId });
  }, [standalone, tournament, myRegistrationRows, currentUserId]);

  // Organizer/co-owner/scorer — gates every match-management control this
  // module ports from ScheduleTab.jsx (calendar reschedule, delete, Add
  // Round/Match, the role-aware status badge). Reuses access.js's own
  // helper rather than re-deriving "who can manage this tournament's
  // schedule" a second time.
  const canManageThisSchedule = !standalone && canManageMatches(currentUserId, tournamentVO);
  // Start/Complete Tournament use their OWN, narrower role sets in
  // production (ScheduleTab.jsx) — Start allows organizer OR scorer (not a
  // co-owner who's neither), Complete allows organizer OR co-owner (not a
  // scorer who's neither) — different enough from canManageThisSchedule's
  // broader OR-of-all-three that they're worth their own checks rather
  // than reusing it for both.
  const canStartTournament = !standalone && (isOrganizer(currentUserId, tournamentVO) || isScorer(currentUserId, tournamentVO));
  const canCompleteTournament = !standalone && (isOrganizer(currentUserId, tournamentVO) || isCoOwner(currentUserId, tournamentVO));

  // Backend-driven "search for a specific real person" (see FiltersModal.jsx)
  // — reuses the same person-search endpoint the Squads "Add/Invite Members"
  // flow already calls (`GET /user?search=`, see AddInviteMembers.jsx),
  // rather than inventing a new one. Always the real API, standalone Explore
  // Formats included — one code path regardless of mode, same as My Games:
  // mockParticipants.json plants this session's real user id on a few mock
  // entries specifically so a real search against the real endpoint still
  // finds someone to match against there (see involvedUserIds in
  // scheduleRows.js). The 4-character minimum matches that same existing
  // AddInviteMembers.jsx convention, not an arbitrary choice.
  const searchUsers = useCallback(async (query) => {
    if (!query || query.trim().length < 4) return [];
    const result = await apiCall(`/user?search=${encodeURIComponent(query.trim())}&page=1&limit=10`);
    return (result?.data || []).map((u) => ({
      id: u.id,
      name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.userHandle || u.email,
      email: u.email,
    }));
  }, []);

  // Applies a backend-loaded schedule's state — split out from the effect
  // below so a check-in/Start Scoring action (see `refreshLiveState`) can
  // reuse the exact same application logic on demand, not just at mount.
  //
  // `preserveOverrides` — true for `refreshLiveState`'s on-demand calls,
  // false (default) for the initial mount-time load. A ROUND's override only
  // gets "baked into the saved rows" one match at a time, as each is
  // individually Started (see startScoring's `gameSettings: row.settings`
  // POST) — it's never applied to the whole round atomically. Unconditionally
  // wiping roundOverrides/rowOverrides/matchOrderOverrides here meant Starting
  // the FIRST match in a round (which fires refreshLiveState right after)
  // erased the round's override for every match after it — organizer sets
  // Best of 5 -> 1 for a round, starts match 1 (correctly gets bestOf 1),
  // starts match 2 (silently back to the sport default, override gone).
  // Only the initial mount load should reset these: at that point nothing
  // local has been built up yet this session, so there's nothing to lose,
  // and it's still correct to prefer a genuinely fresh backend load over
  // whatever a stale localStorage draft (a previous, unrelated session) left
  // behind — see the mount effect's own doc comment below.
  const applyLoadedSchedule = useCallback((saved, { preserveOverrides = false } = {}) => {
    setConfig((prev) => ({ ...prev, formatId: saved.formatId, ...saved.config }));
    setSchedule(saved.schedule);
    setViewMode(saved.viewState.viewMode || null);
    setTieFormat(saved.viewState.tieFormat || TIE_FORMATS.SINGLE_MATCH);
    setTieOverrides(saved.viewState.tieOverrides || {});
    setFilters(saved.viewState.filters || EMPTY_FILTERS);
    if (!preserveOverrides) {
      // A backend load's overrides are already baked into the saved rows
      // (see scheduleApi.js) — any raw override maps left over from a local
      // draft key off THAT draft's own match/round ids, which the backend
      // load just replaced, so they're stale dead weight at best.
      setMatchOrderOverrides({});
      setRoundOverrides({});
      setRowOverrides({});
    }
    setStep(WIZARD_STEPS.RESULT);
  }, []);

  // On first mount in real-tournament mode, the backend always wins
  // whenever it actually has a saved schedule for this tournament — once
  // Save Schedule has ever succeeded, the database IS the source of truth
  // for "what does this tournament's schedule actually look like" (see
  // scheduleApi.js's doc comment on why overrides don't need separate
  // reapplication: the saved rows already reflect them), even over a
  // NEWER local draft (e.g. adjusting Legs after a save without saving
  // again — that local tweak is deliberately provisional until it's saved;
  // a refresh reverts to whatever's actually persisted, not the unsaved
  // draft). localStorage's only remaining job is protecting a schedule
  // that has NEVER been saved at all yet — `loadSavedSchedule` naturally
  // returns null in that case, and the `.catch`/falsy-`saved` branch below
  // leaves whatever localStorage/wizard-start already seeded untouched, so
  // that never-saved-anywhere work still survives a refresh. Guarded so a
  // slow-resolving load can't clobber progress made in the wizard by the
  // time it comes back, and so Explore Formats (standalone) never attempts
  // this at all.
  useEffect(() => {
    if (standalone) return;
    let cancelled = false;
    loadSavedSchedule(tournamentId).then((saved) => {
      if (cancelled) return;
      if (saved) applyLoadedSchedule(saved);
    }).catch(() => {
      // No saved schedule, or this tournament/format isn't loadable yet —
      // silently fall back to whatever localStorage/wizard-start already
      // seeded, same as if this call never happened.
    }).finally(() => {
      // Whatever the outcome, the blank period is over: either a schedule
      // was applied (step is RESULT) or there genuinely isn't one and the
      // wizard is the correct screen. Runs on the cancelled path too — the
      // component is unmounting, so the setState is a no-op either way.
      if (!cancelled) setIsLoadingSaved(false);
    });
    return () => {
      cancelled = true;
    };
    // Runs once per tournamentId, deliberately not re-run on standalone
    // (fixed for the hook's lifetime) or any wizard state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  // On-demand version of the mount effect above — called after check-in/
  // Start Scoring actions change this schedule's live state server-side, so
  // the schedule view reflects the new checkin_status without a full page
  // reload. No cancellation guard (unlike the mount effect): this only ever
  // fires from a deliberate, already-in-flight user action, not a mount
  // that could unmount before it resolves.
  const refreshLiveState = useCallback(() => {
    return loadSavedSchedule(tournamentId)
      .then((saved) => {
        if (saved) applyLoadedSchedule(saved, { preserveOverrides: true });
        return !!saved;
      })
      .catch(() => false);
  }, [tournamentId, applyLoadedSchedule]);

  // `tournamentContext` seeds the initial config above, but that's a
  // useState initializer — it only runs once at mount. `participantsSummary`
  // is fetched async by the parent (TournamentDetailPage) and often isn't
  // resolved yet at that instant, so this keeps `participantCount` synced to
  // it as registrations come in. Guarded on `!config.formatId` and
  // `!stored?.participantCount` so it only patches before selectFormat has
  // locked in real values, or a schedule was already generated/saved — never
  // clobbers a real decision. It's safe to keep re-syncing participantCount
  // itself on every tournamentContext change (rather than freezing after the
  // first fill, like sportType/sportPlayedBy/participantType below still do)
  // because nothing in the real-tournament flow ever lets a user hand-edit
  // it — ConfigureParametersStep only displays it, TestDataStep's editable
  // count field is standalone-only (tournamentContext is null there). Without
  // this, registering more teams after this component's first mount (even
  // mid-session, tab kept alive via visitedTabs) left the Format step's
  // eligibility list stuck on whatever headcount existed at that first mount.
  useEffect(() => {
    if (!tournamentContext || config.formatId || stored?.participantCount) return;
    updateConfig((prev) => ({
      sportType: prev.sportType || tournamentContext.sportName || null,
      sportPlayedBy: prev.sportPlayedBy || tournamentContext.sportPlayedBy || null,
      participantType: prev.participantType === 'singles' ? tournamentContext.participantsType || prev.participantType : prev.participantType,
      participantCount: tournamentContext.enrolledCount || null,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentContext, config.formatId, stored]);

  const format = config.formatId ? getFormatById(config.formatId) : null;
  const isConfiguring = step === WIZARD_STEPS.PARAMETERS || step === WIZARD_STEPS.GROUPS;
  // A fixed-2-sides format (Dual Meet) with exactly 2 teams has nothing to
  // group or assign — those 2 teams already ARE the two competing sides —
  // so the Groups step is skipped entirely for that one trivial case.
  const needsGroupsStep = !!(
    format?.needsGrouping && !(format.fixedGroupCount && config.participantCount === format.fixedGroupCount)
  );

  // Save Schedule (see scheduleApi.js) — real-tournament mode only, and
  // only for formats with a save/load adapter wired up so far. `isSaving`/
  // `saveError`/`lastSavedAt` are UI feedback state for GeneratedScheduleStep's
  // button, not persisted anywhere themselves.
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const canSaveSchedule = !standalone && !!format && isSaveSupported(format.id);

  const saveSchedule = useCallback(async () => {
    if (!format || !schedule) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await saveScheduleToBackend({
        tournamentId,
        formatId: format.id,
        schedule,
        config,
        viewMode,
        tieFormat,
        tieOverrides,
        filters,
        roundOverrides,
        rowOverrides,
        matchOrderOverrides,
      });
      setLastSavedAt(Date.now());
      // Re-hydrate from the backend immediately — without this, `roundsArrays`
      // stays whatever was locally generated (no row has `.liveData` yet),
      // so `allLiveMatches` reads as empty and `hasSavedSchedule` (gating
      // Start Tournament, Complete Tournament, Add Round/Match, Start
      // Scoring) stays false until something else happens to refetch —
      // in practice, only a manual page reload. A save having actually
      // succeeded is exactly the "hasSavedSchedule" is now true.
      await refreshLiveState();
      // The local draft's only job is protecting UNSAVED work from the
      // mount-time backend-load guard below — once a save actually lands,
      // the backend IS that work, and is now the more authoritative copy
      // (baked court/settings/order overrides, canonical ids). Leaving the
      // local draft in place let it silently drift from what's actually
      // saved (e.g. changing Legs after a save regenerates and rewrites the
      // local draft — see generate() — without touching the backend), and a
      // refresh would then show that stale/diverged local draft instead of
      // what Save Schedule actually persisted. Clearing it here means a
      // refresh right after a save always re-derives from the backend, and
      // the local draft only reappears once there's genuinely new unsaved
      // work (the next generate()/override call rewrites it).
      writeStored(tournamentId, null);
    } catch (e) {
      setSaveError(e.message || 'Could not save schedule');
    } finally {
      setIsSaving(false);
    }
  }, [format, schedule, tournamentId, config, viewMode, tieFormat, tieOverrides, filters, roundOverrides, rowOverrides, matchOrderOverrides, refreshLiveState]);

  // Start Scoring — a literal port of ScheduleTab.jsx's own
  // `handleStartScoring`, just triggered from this module's MatchDetailModal
  // instead of GameCard. `row` is a resolved MatchListView row (see
  // scheduleRows.js) — `row.liveData.id` is that match's real backend
  // `tournament_rounds` id (only present once this schedule has been
  // saved — see scheduleApi.js's `liveData` doc comment). Deliberately NOT
  // `row.key`: for an MLP tie's discipline sub-rows, `key` is a composite
  // `${matchId}-${gameType}` (the backend still only ever saves the whole
  // tie as one row), so it's never a valid id to start/check in a game with
  // — only `liveData.id` is. `row.liveData` is otherwise its raw snake_case
  // backend fields, already shaped exactly like the `game` object
  // production's own handlers expect, so no adapting is needed to reuse
  // `matchHandlers.buildTeamsDataFromGame` here.
  //
  // Sequence: mark the round in_progress -> create the actual `game_common`
  // scoring record and claim this user as its sole scorer (both
  // best-effort — a live tab still opens without them, same as production
  // tolerates) -> open the live tab -> THEN best-effort refresh the
  // schedule view's own data in the background. That refresh used to fire
  // right after startGame, unawaited — since it's a real refetch (not
  // instant), it routinely landed WHILE the schedule list was still the
  // visible screen (the live/fullscreen view hadn't mounted yet), so the
  // list would visibly re-render with the row's new status for a beat
  // before the actual scoring screen took over — a flicker of "wrong
  // screen" the user was seeing on every Start Scoring tap. Firing it after
  // the hand-off keeps the schedule data fresh for whenever the user goes
  // back to it, without ever being on screen mid-transition.
  //
  // The live tab itself opens via `onOpenLiveTab` (see ScheduleBuilderTab.jsx, threaded from
  // TournamentDetailPage.jsx's existing `handleOpenLiveTab`/`liveGameTabs`
  // mechanism — unchanged, this module only needed to start calling it).
  // Shared by startScoring (fresh start, real gameCommonId) and
  // openLiveGame (resuming an already-live/completed match, no
  // gameCommonId) — literal port of ScheduleTab.jsx's own
  // `handleOpenLiveTab(game, gameCommonId = null)`, which both its Live
  // badge (no gameCommonId — just resuming) and its post-start flow (real
  // gameCommonId) call the same way.
  //
  // `openHandler` defaults to the page-level `onOpenLiveTab` (opens a real
  // tab on TournamentDetailPage) but callers can pass their own — this is
  // what lets FullscreenScheduleOverlay capture the same payload into its
  // own embedded 'game' sub-view instead of a page tab, without this hook
  // needing to know anything about fullscreen at all.
  const openLiveTabForRow = useCallback(async (row, gameCommonId = null, openHandler = onOpenLiveTab) => {
    if (!openHandler || !row?.liveData) return;
    const matchHandlers = new MatchHandlers({ user });
    const teamsData = await matchHandlers.buildTeamsDataFromGame(row.liveData);
    const label = `${row.liveData.participant_a_name || 'Team A'} vs ${row.liveData.participant_b_name || 'Team B'}`;
    const shortLabel = label.length > 20 ? `${label.substring(0, 17)}...` : label;
    openHandler({
      gameId: row.liveData.id,
      gameCommonId,
      label: shortLabel,
      // Untruncated version — `label` above is sized for TournamentDetailPage's
      // page-level tab chip (see handleOpenLiveTab), too narrow a space for
      // a full team/player name. FullscreenScheduleOverlay's Previous/Next
      // bar has room for the real thing and reads this instead.
      fullLabel: label,
      sportName: tournament?.sportType?.sportName || tournament?.sport_typea?.sport_name || tournament?.sportName,
      teamsData,
    });
  }, [onOpenLiveTab, user, tournament]);

  const startScoring = useCallback(async (row, openHandler) => {
    if (!row?.liveData) return;
    const roundId = row.liveData.id;
    try {
      await tournamentScheduleAPI.startGame(roundId);
    } catch (err) {
      console.warn('[useScheduleBuilder] startGame failed, proceeding anyway:', err);
    }

    let gameCommonId = null;
    const eventId = row.liveData.event_id;
    const sportTypeId = row.liveData.sport_type_id;
    if (eventId && sportTypeId) {
      try {
        const gameData = await apiCall('/games', {
          method: 'POST',
          body: JSON.stringify({
            eventId,
            sportTypeId,
            tournamentRoundId: roundId,
            status: 'setup',
            // `row.settings` is this row's EFFECTIVE custom settings (gear
            // icon's "Use custom settings for this match/round" — see
            // scheduleRows.js's resolveOverrides), null unless actually
            // overridden. Without this, createGame() (gameController.js)
            // defaults gameSettings to {} and the organizer's override
            // (e.g. bestOf 5 -> 1) never reaches the created game_common —
            // it silently scores with sport defaults instead.
            ...(row.settings ? { gameSettings: row.settings } : {}),
          }),
        });
        gameCommonId = gameData.id;
        // See ScheduleTab: proceeding either way is deliberate, GameComponent
        // resolves the real scorer by polling and the server refuses the rest.
        await claimScorerRole(gameCommonId);
      } catch (err) {
        console.warn('[useScheduleBuilder] Failed to create game_common:', err);
      }
    }

    await openLiveTabForRow(row, gameCommonId, openHandler);
    // Fire-and-forget, deliberately AFTER hand-off — see the doc comment above.
    refreshLiveState();
  }, [refreshLiveState, openLiveTabForRow]);

  // Resuming an already-started (live) or viewing an already-finished
  // (completed) match — no startGame/game_common/claim-scorer calls, those
  // already happened the first time. Row click for these two statuses goes
  // straight here instead of opening Match Details, matching production's
  // own row badge (ScheduleTab.jsx's Live button calls `handleOpenLiveTab`
  // directly, no confirm/detail step in between).
  const openLiveGame = useCallback(async (row, openHandler) => {
    await openLiveTabForRow(row, null, openHandler);
  }, [openLiveTabForRow]);

  // Start/Complete Tournament — literal ports of ScheduleTab.jsx's own
  // `handleStartTournament`/`handleCompleteTournament`: a plain status
  // PATCH on the tournament itself, then a full page reload. The reload
  // (not just refreshLiveState) is deliberate — `tournament` here is a prop
  // this hook was handed by TournamentDetailPage.jsx, which owns the fetch
  // that produced it; a status flip needs to propagate to every OTHER tab
  // on that page too (Tournament Info, Enrolled, ...), not just this one,
  // and production solves that the exact same way rather than inventing
  // cross-tab refetch plumbing for a rare, deliberate, whole-page action.
  const [isUpdatingTournamentStatus, setIsUpdatingTournamentStatus] = useState(false);
  const [tournamentStatusError, setTournamentStatusError] = useState(null);
  const startTournament = useCallback(async () => {
    setIsUpdatingTournamentStatus(true);
    setTournamentStatusError(null);
    try {
      await tournamentAPI.update(tournamentId, { status: 'in_progress' });
      window.location.reload();
    } catch (e) {
      setTournamentStatusError(e.message || 'Could not start the tournament');
      setIsUpdatingTournamentStatus(false);
    }
  }, [tournamentId]);
  const completeTournament = useCallback(async () => {
    setIsUpdatingTournamentStatus(true);
    setTournamentStatusError(null);
    try {
      await tournamentAPI.update(tournamentId, { status: 'completed' });
      window.location.reload();
    } catch (e) {
      setTournamentStatusError(e.message || 'Could not complete the tournament');
      setIsUpdatingTournamentStatus(false);
    }
  }, [tournamentId]);

  // Per-match reschedule (the calendar icon) — literal port of
  // ScheduleTab.jsx's own `handleSaveMatch`: same three fields
  // (start_time/end_time/address), same AddEventModal.jsx reused as the
  // actual form (see MatchRow.jsx). `eventData` is exactly what that
  // modal's own `onSave` hands back — `date`/`startTime`/`endTime` are
  // plain strings it already validated, so building real Date objects here
  // is all that's left to do before sending.
  const updateMatchSchedule = useCallback(async (row, eventData) => {
    if (!row?.liveData) return;
    await tournamentScheduleAPI.updateGame(row.liveData.id, {
      start_time: new Date(`${eventData.date}T${eventData.startTime}`).toISOString(),
      end_time: new Date(`${eventData.date}T${eventData.endTime}`).toISOString(),
      address: eventData.address || 'TBD',
    });
    await refreshLiveState();
  }, [refreshLiveState]);

  // Delete a single match — literal port of ScheduleTab.jsx's own
  // `handleDeleteMatch`. Confirmation lives in MatchRow.jsx (same
  // `window.confirm` production already uses), not here.
  const deleteMatchRow = useCallback(async (row) => {
    if (!row?.liveData) return;
    await tournamentScheduleAPI.deleteMatch(row.liveData.id);
    await refreshLiveState();
  }, [refreshLiveState]);

  // Add Round — literal port of ScheduleTab.jsx's own `handleAddRound`,
  // except the next round_number is the real max + 1 (see scheduleApi.js's
  // `realRoundNumber` doc comment) rather than production's own
  // `apiRounds.length + 1`, which collides once any round has ever been
  // deleted (a gap in round_number no longer equals the round count).
  const addRound = useCallback(async ({ name, type }) => {
    const existingRoundNumbers = (schedule ? getScheduleRoundsArrays(format, schedule) : [])
      .flat()
      .map((round) => round?.realRoundNumber)
      .filter((n) => Number.isFinite(n));
    const nextRoundNumber = existingRoundNumbers.length ? Math.max(...existingRoundNumbers) + 1 : 1;
    await tournamentScheduleAPI.createRound(tournamentId, {
      round_name: name,
      round_type: type,
      round_number: nextRoundNumber,
    });
    await refreshLiveState();
  }, [tournamentId, format, schedule, refreshLiveState]);

  // Add Match — literal port of ScheduleTab.jsx's own `handleAddMatch`.
  // `round` is the round object the "+" icon was clicked on (see
  // RoundSection.jsx) — its real `realRoundNumber`, not the display `round`
  // index, is what the backend needs to slot the new match into.
  const addMatch = useCallback(async (round, { participantAType, participantAId, participantBType, participantBId }) => {
    await tournamentScheduleAPI.createMatch(tournamentId, {
      round_number: round.realRoundNumber,
      participant_a_type: participantAType,
      participant_a_id: participantAId,
      participant_b_type: participantBType,
      participant_b_id: participantBId,
    });
    await refreshLiveState();
  }, [tournamentId, refreshLiveState]);

  // Assign the two participants of an EXISTING match that doesn't have any
  // yet — every `createRound` call creates one placeholder match row
  // (round + match are the same tournament_rounds row in this schema, see
  // createRound's `match_number: 1`) with participant_a_id/participant_b_id
  // left null, for every round type, not just knockout/playoff. There's no
  // separate "assign" endpoint — `updateGame` already accepts these same
  // participant fields (used elsewhere for the Team Pairing settings modal),
  // so this is just that same PATCH with only the participant fields set.
  const assignMatchParticipants = useCallback(async (row, { participantAType, participantAId, participantBType, participantBId }) => {
    if (!row?.liveData) return;
    await tournamentScheduleAPI.updateGame(row.liveData.id, {
      participant_a_type: participantAType,
      participant_a_id: participantAId,
      participant_b_type: participantBType,
      participant_b_id: participantBId,
    });
    await refreshLiveState();
  }, [refreshLiveState]);

  // Every enrolled participant in this tournament, regardless of whether
  // they made it into the CURRENT generated schedule — Add Match needs the
  // full pool to pick from, not just `config.participantCount`'s worth.
  // `getParticipants` (realParticipantsService.js) returns everything when
  // `count` is nullish rather than slicing, so this needs no new provider
  // method.
  const fetchAllParticipants = useCallback(() => {
    return Promise.resolve(participantsProvider.getParticipants(config.participantType, null, config.playersPerTeam));
  }, [participantsProvider, config.participantType, config.playersPerTeam]);

  // TestDataStep's count bounds need SOMETHING from the participants
  // provider without generating a whole schedule — computed here so it can
  // stay entirely provider-agnostic, reading this off the hook instead of
  // calling participantsProvider itself. Provider calls are always awaited
  // (mock resolves instantly; the real provider is a genuine network
  // request), so this is state populated by an effect rather than a plain
  // useMemo, guarded with a `cancelled` flag so a fast-changing type while a
  // fetch is in flight can't apply a stale result after the fact.
  const [maxParticipantsAvailable, setMaxParticipantsAvailable] = useState(0);
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(participantsProvider.getMaxParticipants(config.participantType)).then((max) => {
      if (!cancelled) setMaxParticipantsAvailable(max);
    });
    return () => {
      cancelled = true;
    };
  }, [participantsProvider, config.participantType]);

  const selectFormat = useCallback((id) => {
    setError(null);
    const f = getFormatById(id);
    setConfig((prev) => {
      // A participant count already exists whenever it was seeded up front —
      // Test Data (standalone) or the real tournament's enrolled count
      // (tournament mode) — and picking a format shouldn't throw that away;
      // only formats picked with no prior count at all fall back to a default.
      const participantCount = prev.participantCount || DEFAULT_COUNT_BY_FORMAT[id] || f?.minParticipants || 8;
      // Team-roster formats (cross_group) always schedule real team
      // rosters — force the participant pool to 'teams' regardless of
      // whatever singles/doubles was previously selected for a different
      // format. Every OTHER format is fine with 'teams' too (MLP eligibility
      // is a match-level property, not tied to one structure — see
      // tieFormats.getScheduleRoundsArrays), so switching formats never
      // resets an already-chosen participantType away from 'teams' — EXCEPT
      // a `disallowsTeams` format (ladder), whose one-slot-per-court-seat
      // model doesn't fit a multi-player team roster at all.
      const participantType = f?.requiresTeamRoster
        ? 'teams'
        : f?.disallowsTeams && prev.participantType === 'teams'
          ? 'singles'
          : prev.participantType;
      const groupingConfig = f?.needsGrouping
        ? {
            groupCount: f.fixedGroupCount || suggestGroupCount(DEFAULT_COUNT_BY_FORMAT[id] || f.minParticipants),
            advancePerGroup: f.fixedAdvancePerGroup || 2,
          }
        : null;
      // Default: one ladder round per court (matches the confirmed 2 courts
      // -> 2 rounds, 4 courts -> 4 rounds pattern) — kept in sync with
      // participant count by the effect below until the organizer adjusts it
      // by hand (see ladderRoundsUserAdjusted).
      const ladderRounds = f?.supportsLadderRounds ? Math.max(1, Math.ceil(participantCount / 4)) : prev.ladderRounds;
      return {
        ...prev,
        formatId: id,
        participantCount,
        participantType,
        legs: 1,
        groupingConfig,
        // A previous format's custom group names are keyed by index into
        // that format's own group count/meaning — never valid carried over
        // into a different format's grouping.
        groupNames: {},
        ladderRounds,
        ladderRoundsUserAdjusted: false,
      };
    });
  }, []);

  // Keep the group-count suggestion sensible as the participant count changes,
  // but only while still configuring (don't fight the user's edits once a
  // schedule exists). `fixedGroupCount` formats (Dual Meet — always exactly 2
  // sides) never rescale with participant count, unlike
  // group_knockout/regional_bracket where more participants sensibly means
  // more pools/regions.
  useEffect(() => {
    if (format?.needsGrouping && config.participantCount && isConfiguring) {
      updateConfig((prev) => ({
        groupingConfig: {
          groupCount: format.fixedGroupCount || (prev.groupingConfig?.userAdjustedGroups ? prev.groupingConfig.groupCount : suggestGroupCount(config.participantCount)),
          advancePerGroup: format.fixedAdvancePerGroup || prev.groupingConfig?.advancePerGroup || 2,
          userAdjustedGroups: prev.groupingConfig?.userAdjustedGroups || false,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.participantCount, format?.id, isConfiguring]);

  // Same pattern as the group-count suggestion above, for ladder's "one round
  // per court" default — recomputed as participant count changes, unless the
  // organizer has already adjusted it by hand.
  useEffect(() => {
    if (format?.supportsLadderRounds && config.participantCount && isConfiguring && !config.ladderRoundsUserAdjusted) {
      updateConfig({ ladderRounds: Math.max(1, Math.ceil(config.participantCount / 4)) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.participantCount, format?.id, isConfiguring, config.ladderRoundsUserAdjusted]);

  // Recomputes the default (serpentine-seeded) group assignment whenever its
  // real inputs change — participant pool or group/region count. Deliberately
  // does NOT depend on groupingConfig's other fields (e.g. advancePerGroup)
  // or on groupAssignments itself, so a manual move/randomize edit survives
  // unrelated config tweaks and isn't clobbered by its own update.
  useEffect(() => {
    let cancelled = false;
    if (format?.needsGrouping && config.groupingConfig?.groupCount && config.participantCount && isConfiguring) {
      Promise.resolve(participantsProvider.getParticipants(config.participantType, config.participantCount, config.playersPerTeam)).then(
        (participants) => {
          if (!cancelled) setGroupAssignments(assignGroups(participants, config.groupingConfig.groupCount));
        }
      );
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format?.id, config.groupingConfig?.groupCount, config.participantCount, config.participantType, config.playersPerTeam, isConfiguring, participantsProvider]);

  const moveParticipant = useCallback((participantId, toGroupIndex) => {
    setGroupAssignments((prev) => {
      if (!prev) return prev;
      const participant = prev.flat().find((p) => p.id === participantId);
      if (!participant) return prev;
      return prev.map((group, idx) => {
        const withoutMoved = group.filter((p) => p.id !== participantId);
        return idx === toGroupIndex ? [...withoutMoved, participant] : withoutMoved;
      });
    });
  }, []);

  const randomizeGroups = useCallback(async () => {
    if (!format?.needsGrouping || !config.groupingConfig?.groupCount || !config.participantCount) return;
    const participants = await participantsProvider.getParticipants(config.participantType, config.participantCount, config.playersPerTeam);
    setGroupAssignments(assignGroups(shuffleParticipants(participants), config.groupingConfig.groupCount));
  }, [format, config.groupingConfig, config.participantCount, config.participantType, config.playersPerTeam, participantsProvider]);

  // `overrides` lets a caller regenerate with a config value changed in the
  // same action — e.g. the Result screen's Legs control (GeneratedScheduleStep)
  // calling `generate({ legs: 3 })` — without a separate updateConfig call
  // first, which would regenerate against the stale pre-update config since
  // this callback closes over `config` from render time, not the next one.
  const generate = useCallback(async (overrides = {}) => {
    const effectiveConfig = { ...config, ...overrides };
    if (!format || !effectiveConfig.participantCount) return;
    try {
      const participants = await participantsProvider.getParticipants(effectiveConfig.participantType, effectiveConfig.participantCount, effectiveConfig.playersPerTeam);
      const options = format.needsGrouping
        ? {
            groupCount: effectiveConfig.groupingConfig?.groupCount,
            advancePerGroup: effectiveConfig.groupingConfig?.advancePerGroup,
            groupAssignments: groupAssignments || undefined,
            groupNames: effectiveConfig.groupNames,
          }
        : {};
      if (format.supportsLegs) options.legs = effectiveConfig.legs;
      if (format.supportsLadderRounds) options.ladderRounds = effectiveConfig.ladderRounds;
      const result = generateSchedule(format.id, participants, options);
      const defaultViewMode =
        format.id === 'page_playoff' ? 'league'
          : format.id === 'cross_group' ? 'matches'
          : format.id === 'ladder' ? 'ladder'
          : format.resultView === 'hybrid' ? 'groups' : 'bracket';
      // A freshly-generated schedule may have different (or no) MLP-eligible
      // ties, teams, or rosters than the last one, so a genuinely fresh
      // generate (empty `overrides` — Format/Groups steps' "Generate
      // Schedule") starts tie format/overrides at their defaults rather than
      // carry over a selection that might no longer apply. A Legs-only
      // regenerate (GeneratedScheduleStep's Legs control, the only caller
      // that ever passes overrides today) is different: it's the exact same
      // participants/teams/rosters, just more or fewer meetings between each
      // pairing — an existing MLP selection is still just as valid against
      // the new leg count (tieOverrides are keyed by team id, not by
      // round/leg), so resetting it here was actively destroying a live MLP
      // setup every time someone adjusted Legs.
      const isLegsOnlyRegenerate = Object.keys(overrides).length > 0 && Object.keys(overrides).every((k) => k === 'legs');
      const nextTieFormat = isLegsOnlyRegenerate ? tieFormat : TIE_FORMATS.SINGLE_MATCH;
      const nextTieOverrides = isLegsOnlyRegenerate ? tieOverrides : {};
      setSchedule(result);
      setViewMode(defaultViewMode);
      setTieFormat(nextTieFormat);
      setTieOverrides(nextTieOverrides);
      setMatchOrderOverrides({});
      setRoundOverrides({});
      setRowOverrides({});
      setFilters(EMPTY_FILTERS);
      setStep(WIZARD_STEPS.RESULT);
      setError(null);
      if (Object.keys(overrides).length) setConfig(effectiveConfig);
      writeStored(tournamentId, {
        ...effectiveConfig,
        schedule: result,
        viewMode: defaultViewMode,
        tieFormat: nextTieFormat,
        tieOverrides: nextTieOverrides,
        matchOrderOverrides: {},
        roundOverrides: {},
        rowOverrides: {},
        filters: EMPTY_FILTERS,
      });
    } catch (e) {
      setError(e.message || 'Could not generate schedule');
    }
  }, [format, config, groupAssignments, tournamentId, participantsProvider, tieFormat, tieOverrides]);

  // Only reached in standalone mode — see WIZARD_STEPS.TEST_DATA.
  const proceedFromTestData = useCallback(() => setStep(WIZARD_STEPS.FORMAT), []);
  // Only meaningful in standalone mode too — Format is the first step
  // otherwise, with nothing before it to go back to (see SelectFormatStep,
  // which only renders this Back button when `standalone` is true).
  const backToTestData = useCallback(() => setStep(WIZARD_STEPS.TEST_DATA), []);
  // Whether every input the Parameters screen collects is already
  // available — participantCount and (for teams) playersPerTeam are always
  // seeded up front, from Test Data (standalone) or the real tournament's
  // own data (tournamentContext), so by the time a format's picked they're
  // already real values, not placeholders. Legs is never asked here at all
  // — it always starts at its default of 1 and is only ever adjusted
  // afterward, from a Legs control on the Result screen (see generate's
  // `overrides` param) — so it doesn't factor into this check. Ladder
  // rounds is the one real exception: still needs Parameters shown, to have
  // the organizer set it deliberately before the (currently one-shot, not
  // regenerable) Round 1 gets built. Exposed (not just used inline by
  // proceedFromFormat below) so the Format step's "Next" button can preview
  // what it's actually about to do.
  const canSkipParameters = useMemo(() => {
    if (!format || format.supportsLadderRounds) return false;
    const hasCount = !!config.participantCount && config.participantCount > 0;
    const hasRoster = config.participantType !== 'teams' || (!!config.playersPerTeam && config.playersPerTeam > 0);
    return hasCount && hasRoster;
  }, [format, config.participantCount, config.participantType, config.playersPerTeam]);

  // What the Format step's "Next" button reads and does — mirrors
  // ConfigureParametersStep's own "Set Groups" / "Generate Schedule" wording
  // for the same two outcomes, plus "Configure Parameters" for the case
  // where that screen still has something (namely ladder rounds) left to collect.
  const formatStepNextLabel = !format
    ? 'Next'
    : !canSkipParameters
      ? 'Configure Parameters'
      : needsGroupsStep
        ? 'Set Groups'
        : 'Generate Schedule';

  const proceedFromFormat = useCallback(() => {
    if (!canSkipParameters) {
      setStep(WIZARD_STEPS.PARAMETERS);
    } else if (needsGroupsStep) {
      setStep(WIZARD_STEPS.GROUPS);
    } else {
      generate();
    }
  }, [canSkipParameters, needsGroupsStep, generate]);
  const backToFormat = useCallback(() => setStep(WIZARD_STEPS.FORMAT), []);
  // Mirrors proceedFromFormat: when canSkipParameters is true, Parameters
  // was never actually part of the forward path to Groups (proceedFromFormat
  // jumps straight there), so Back from Groups shouldn't land on it either
  // — it would just be a blank screen with nothing left to configure.
  const backToParameters = useCallback(() => {
    setStep(canSkipParameters ? WIZARD_STEPS.FORMAT : WIZARD_STEPS.PARAMETERS);
  }, [canSkipParameters]);
  // "Next" on the Parameters step either advances to Groups or, for formats
  // that don't need one, generates immediately — this is what decides
  // whether that button reads "Set Groups" or "Generate Schedule".
  const proceedFromParameters = useCallback(() => {
    if (needsGroupsStep) setStep(WIZARD_STEPS.GROUPS);
    else generate();
  }, [needsGroupsStep, generate]);

  const changeViewMode = useCallback((mode) => {
    setViewMode(mode);
    const current = readStored(tournamentId);
    if (current) writeStored(tournamentId, { ...current, viewMode: mode });
  }, [tournamentId]);

  const changeTieFormat = useCallback((mode) => {
    setTieFormat(mode);
    const current = readStored(tournamentId);
    if (current) writeStored(tournamentId, { ...current, tieFormat: mode });
  }, [tournamentId]);

  const applyTieOverrides = useCallback((overrides) => {
    setTieOverrides(overrides);
    const current = readStored(tournamentId);
    if (current) writeStored(tournamentId, { ...current, tieOverrides: overrides });
  }, [tournamentId]);

  // From FiltersModal's "Apply Filters" — replaces the whole filter object
  // at once (a staged draft committed in one action), same pattern as
  // applyTieOverrides above.
  const applyFilters = useCallback((next) => {
    setFilters(next);
    const current = readStored(tournamentId);
    if (current) writeStored(tournamentId, { ...current, filters: next });
  }, [tournamentId]);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    const current = readStored(tournamentId);
    if (current) writeStored(tournamentId, { ...current, filters: EMPTY_FILTERS });
  }, [tournamentId]);

  // Every distinct court/round-label/group-label/participant the CURRENT
  // schedule actually contains — what FiltersModal's checklists offer,
  // computed fresh whenever the schedule changes rather than kept in sync by
  // hand. `getScheduleRoundsArrays` already knows how to flatten any
  // format's differently-shaped schedule into "every rounds array it has"
  // (see tieFormats.js) — reused here for the exact same reason it exists
  // for MLP eligibility: a filter option shouldn't care which structure
  // scheduled the match, only that the match exists somewhere in it.
  const filterOptions = useMemo(() => {
    if (!format || !schedule) return { rounds: [], groups: [], participants: [] };
    const roundsArrays = getScheduleRoundsArrays(format, schedule);
    const rounds = [];
    const groups = [];
    const participants = new Map();
    roundsArrays.forEach((rounds_) => {
      (rounds_ || []).forEach((round) => {
        if (round.label && !rounds.includes(round.label)) rounds.push(round.label);
        (round.matches || []).forEach((match) => {
          if (match.groupLabel && !groups.includes(match.groupLabel)) groups.push(match.groupLabel);
          [match.slotA, match.slotB].forEach((entry) => {
            if (entry?.id != null && !entry.isPlaceholder) participants.set(entry.id, entry.name);
          });
        });
      });
    });
    return { rounds, groups, participants: Array.from(participants, ([id, name]) => ({ id, name })) };
  }, [format, schedule]);

  // `listKey` identifies which MatchListView the reorder happened in (see
  // that component's doc comment) — the caller (ScheduleResultView /
  // CrossGroupView / LadderView / GroupStandingsPanel) already knows which
  // list it's rendering, so it's the one that supplies this, not something
  // derived here. `orderedRowKeys` is the row's new order for that one
  // round, already computed by MatchTable's `moveRow`.
  const reorderRow = useCallback((listKey, roundNumber, orderedRowKeys) => {
    setMatchOrderOverrides((prev) => {
      const next = { ...prev, [listKey]: { ...(prev[listKey] || {}), [roundNumber]: orderedRowKeys } };
      const current = readStored(tournamentId);
      if (current) writeStored(tournamentId, { ...current, matchOrderOverrides: next });
      return next;
    });
  }, [tournamentId]);

  // From the round-level gear icon (see RoundSection.jsx). `clearRowKeys` —
  // only passed when the organizer explicitly chose "override all matches"
  // in response to the conflict prompt shown when this round already has
  // rows with their own override — deletes those rows' overrides so the
  // round's value actually takes effect for them (a row override otherwise
  // always wins over its round's, see scheduleRows.js's `resolveOverrides`).
  // Passing `null` as `override` clears the round back to auto/default.
  //
  // `rowLiveDataIds` (RoundSection.jsx's own rows, filtered to ones with a
  // real backend match) — mirrors applyRowOverride's backend-persisting
  // write below, just fanned out to every row in the round instead of one.
  // Without this, a round override only ever lived in this hook's local
  // roundOverrides/localStorage state: real enough to resolve THIS row's own
  // settings for display and for the very first POST /games this settings
  // seeds, but invisible to what happens right after — LiveGameTab.jsx does
  // its own fresh backend fetch (tournamentScheduleAPI.getGame()) once the
  // live game screen mounts, finds no tournament_rounds.overridden_match_
  // settings for this row (round-level overrides never wrote there), falls
  // back to the tournament's sport-wide match_configuration default, and
  // PATCHes THAT over the just-created game — silently reverting whatever
  // the round was actually set to (e.g. Best of 5 -> 1) the moment scoring
  // begins. Same fire-and-forget-on-failure pattern as applyRowOverride.
  const applyRoundOverride = useCallback((listKey, roundNumber, override, clearRowKeys = [], rowLiveDataIds = []) => {
    setRoundOverrides((prevRound) => {
      const nextRound = { ...prevRound, [listKey]: { ...(prevRound[listKey] || {}) } };
      if (override) nextRound[listKey][roundNumber] = override;
      else delete nextRound[listKey][roundNumber];

      if (clearRowKeys.length) {
        setRowOverrides((prevRow) => {
          const nextRow = { ...prevRow, [listKey]: { ...(prevRow[listKey] || {}) } };
          clearRowKeys.forEach((key) => delete nextRow[listKey][key]);
          const current = readStored(tournamentId);
          if (current) writeStored(tournamentId, { ...current, roundOverrides: nextRound, rowOverrides: nextRow });
          return nextRow;
        });
      } else {
        const current = readStored(tournamentId);
        if (current) writeStored(tournamentId, { ...current, roundOverrides: nextRound });
      }
      return nextRound;
    });
    const settingsForBackend = override?.useCustomSettings ? override.settings : null;
    if (rowLiveDataIds.length) {
      Promise.allSettled(
        rowLiveDataIds.map((id) =>
          tournamentScheduleAPI.updateGame(id, { overridden_match_settings: settingsForBackend })
            .catch((err) => { console.warn('[useScheduleBuilder] Failed to persist round settings override for row', id, err); throw err; })
        )
        // Re-fetch once every row's write has landed — without this, each
        // row's own liveData.overridden_match_settings (what scheduleRows.js's
        // resolveOverrides falls back to for the per-match gear icon's own
        // "customized" indicator) stayed stale until the next full page
        // reload, so a match looked un-customized on its own gear icon right
        // after applying round settings even though the round's own icon —
        // and the actual settings a Start Scoring click would use — were
        // already correct.
      ).then(() => refreshLiveState());
    }
  }, [tournamentId, refreshLiveState]);

  // From the match-level gear icon (see MatchRow.jsx). `override` is
  // `{ court, useCustomSettings, settings, playersA, playersB }` — pass
  // `null` to clear a match back to inheriting its round's/auto default.
  //
  // `liveData` (the row's real saved match, when one exists — see
  // scheduleApi.js's buildLiveData) triggers a SECOND, backend-persisting
  // write of just the settings half, to `tournament_rounds.overridden_match_
  // settings` — this is the exact column LiveGameTab.jsx reads (preferring
  // it over the tournament's own match_configuration) when Start Scoring
  // re-fetches the round fresh via tournamentScheduleAPI.getGame(). Without
  // this, the override only ever lived in this hook's local rowOverrides/
  // localStorage state — real enough to redraw this row's own badge, but
  // invisible to the actual scoring session, which loads its settings from
  // the backend round record, not from this frontend's in-memory state.
  // Court/pairing overrides stay purely local (pairing already has its own
  // persistence path — see assignMatchParticipants below), matching
  // production's own handleSaveMatchSettings (ScheduleTab.jsx), which persists
  // only the settings object this same way.
  const applyRowOverride = useCallback((listKey, rowKey, override, liveData) => {
    setRowOverrides((prev) => {
      const next = { ...prev, [listKey]: { ...(prev[listKey] || {}) } };
      if (override) next[listKey][rowKey] = override;
      else delete next[listKey][rowKey];
      const current = readStored(tournamentId);
      if (current) writeStored(tournamentId, { ...current, rowOverrides: next });
      return next;
    });
    if (liveData?.id) {
      tournamentScheduleAPI.updateGame(liveData.id, {
        overridden_match_settings: override?.useCustomSettings ? override.settings : null,
      }).catch((err) => console.warn('[useScheduleBuilder] Failed to persist match settings override:', err));
    }
  }, [tournamentId]);

  // From the Result screen, "Edit" drops back to whichever step actually has
  // something to change, so the organizer can tweak and immediately
  // regenerate without re-walking steps that don't need changes. Mirrors
  // `canSkipParameters` (the forward-path version of this same check) —
  // Parameters has nothing left to show once count/type/roster are already
  // fixed and the format has neither legs nor ladder rounds, so landing
  // there would just be a near-empty screen; Format is the nearest real
  // thing to edit instead.
  const startEditing = useCallback(() => {
    if (needsGroupsStep) setStep(WIZARD_STEPS.GROUPS);
    else if (canSkipParameters) setStep(WIZARD_STEPS.FORMAT);
    else setStep(WIZARD_STEPS.PARAMETERS);
  }, [needsGroupsStep, canSkipParameters]);

  const reset = useCallback(() => {
    setStep(standalone ? WIZARD_STEPS.TEST_DATA : WIZARD_STEPS.FORMAT);
    setConfig({
      formatId: null,
      sportType: standalone ? null : tournamentContext?.sportName || null,
      sportPlayedBy: standalone ? null : tournamentContext?.sportPlayedBy || null,
      participantType: standalone ? 'singles' : tournamentContext?.participantsType || 'singles',
      participantCount: standalone ? null : tournamentContext?.enrolledCount || null,
      playersPerTeam: TEAM_ROSTER_DEFAULT,
      legs: 1,
      groupingConfig: null,
      groupNames: {},
      ladderRounds: null,
      ladderRoundsUserAdjusted: false,
      courts: [],
    });
    setGroupAssignments(null);
    setSchedule(null);
    setViewMode(null);
    setTieFormat(TIE_FORMATS.SINGLE_MATCH);
    setTieOverrides({});
    setMatchOrderOverrides({});
    setRoundOverrides({});
    setRowOverrides({});
    setFilters(EMPTY_FILTERS);
    setError(null);
    writeStored(tournamentId, null);
  }, [tournamentId, standalone, tournamentContext]);

  return {
    step,
    // True only while the mount-time backend load is still in flight — see
    // the useState above. ScheduleBuilderTab renders a placeholder rather
    // than the FORMAT wizard while this is set.
    isLoadingSaved,
    standalone,
    format,
    needsGroupsStep,
    config,
    updateConfig,
    updateCourts,
    maxParticipantsAvailable,
    groupAssignments,
    schedule,
    viewMode,
    tieFormat,
    tieOverrides,
    matchOrderOverrides,
    roundOverrides,
    rowOverrides,
    filters,
    filterOptions,
    currentUserId,
    searchUsers,
    tournamentMatchSettings,
    tournamentVO,
    canManageThisSchedule,
    canStartTournament,
    canCompleteTournament,
    startScoring,
    openLiveGame,
    refreshLiveState,
    startTournament,
    completeTournament,
    isUpdatingTournamentStatus,
    tournamentStatusError,
    updateMatchSchedule,
    deleteMatchRow,
    addRound,
    addMatch,
    assignMatchParticipants,
    fetchAllParticipants,
    error,
    canSaveSchedule,
    saveSchedule,
    isSaving,
    saveError,
    lastSavedAt,
    moveParticipant,
    randomizeGroups,
    selectFormat,
    generate,
    proceedFromTestData,
    backToTestData,
    proceedFromFormat,
    formatStepNextLabel,
    backToFormat,
    backToParameters,
    proceedFromParameters,
    changeViewMode,
    changeTieFormat,
    applyTieOverrides,
    reorderRow,
    applyRoundOverride,
    applyRowOverride,
    applyFilters,
    clearFilters,
    startEditing,
    reset,
  };
};
